import { Disclosure } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import React, { ReactNode } from "react";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

type TGDisclosureProps = {
  title: ReactNode;
  noticeIcon?: ReactNode;
  notice?: string;
  count?: number;
  disabled?: boolean;
  children: ReactNode;
  panelClassName?: string | undefined;
  buttonClassName?: string | undefined;
  wrapperClassName?: string | undefined;
  iconArrowClassName?: string | undefined;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  rightButtons?: () => React.ReactNode;
};

export function TGDisclosure(props: TGDisclosureProps) {
  const {
    title,
    noticeIcon,
    count,
    disabled,
    children,
    panelClassName,
    buttonClassName,
    wrapperClassName,
    defaultOpen = false,
    open: controlledOpen,
    onOpenChange,
    iconArrowClassName,
    rightButtons,
  } = props;

  const isControlled = controlledOpen !== undefined;

  return (
    <Disclosure
      as="div"
      className={twMerge("border-b", disabled && "opacity-50", wrapperClassName)}
      defaultOpen={!isControlled ? defaultOpen : controlledOpen}
    >
      {({ open, close }) => {
        const actualOpen = isControlled ? controlledOpen : open;

        return (
          <>
            <Disclosure.Button
              disabled={disabled}
              className={twMerge(
                actualOpen ? "text-black pt-5 pb-[15px] px-5" : "text-[#637381] p-5",
                "flex w-full text-sm font-medium text-left",
                count && "text-black pt-5 pb-[15px] px-5",
                buttonClassName
              )}
              onClick={() => {
                if (isControlled && onOpenChange) {
                  onOpenChange(!actualOpen);
                }
              }}
            >
              <FlexRow className="gap-x-1 justify-between w-full">
                <span className="flex flex-row">
                  {title}
                  {count ? ` · ${count}` : null}
                </span>
                <FlexCol>
                  <FlexRow className="gap-2 items-center">
                    {noticeIcon}
                    {rightButtons ? rightButtons() : null}
                    <ChevronDownIcon
                      className={twMerge(
                        actualOpen ? "transform rotate-180" : "",
                        "w-5 h-5 text-black",
                        iconArrowClassName
                      )}
                    />
                  </FlexRow>
                </FlexCol>
              </FlexRow>
            </Disclosure.Button>
            {actualOpen && (
              <Disclosure.Panel
                static
                className={twMerge("flex flex-col px-5 text-sm text-gray-500 gap-y-[15px] pb-[15px]", panelClassName)}
              >
                {children}
              </Disclosure.Panel>
            )}
          </>
        );
      }}
    </Disclosure>
  );
}
